import { usStateAbbreviations, usStateNames } from '@side/pantry';
import { castArray } from 'lodash';

/**
 * Google returns the state as an abbreviation (`CA`) most of the time, but as
 * the full name (`California`) when the query itself named the state, so both
 * spellings have to resolve to the abbreviation the cover sheet stores.
 */
const STATE_BY_TERM = new Map([
  ...usStateAbbreviations.map(
    (abbreviation) => [abbreviation.toUpperCase(), abbreviation] as const,
  ),
  ...usStateNames.map(
    (name, index) => [name.toUpperCase(), usStateAbbreviations[index]] as const,
  ),
]);

/**
 * Terms run street → city → state → country, and city names collide with state
 * names (`California, MD`), so the search runs from the country end inwards.
 */
function getPredictionState(
  prediction: google.maps.places.AutocompletePrediction,
) {
  for (let index = prediction.terms.length - 1; index >= 0; index -= 1) {
    const { value } = prediction.terms[index];
    const state =
      typeof value === 'string' && STATE_BY_TERM.get(value.toUpperCase());

    if (state) return state;
  }

  return undefined;
}

export function filterPlacesByStates(
  results: google.maps.places.AutocompletePrediction[] | null,
  states: readonly (string | undefined)[],
): google.maps.places.AutocompletePrediction[] {
  if (!results) return [];

  const allowedStates = new Set(states.map((state) => state?.toUpperCase()));

  return castArray(results).filter((result) => {
    const state = getPredictionState(result);

    return !!state && allowedStates.has(state);
  });
}
